/**
 * DealCard Component
 *
 * Displays a single deal in a card format with stage badge, value, and close date.
 * Used in the MyDealsPage list view.
 *
 * Requirements: Task 6 - My Deals Page
 */

import { Card, CardContent, Badge } from '@bloomcode/ui';
import { Calendar, DollarSign, TrendingUp } from '@bloomcode/icons';
import { formatCurrency } from '@bloomcode/utils';
import type { GetMyDealsQuery } from '@bloomcode/graphql';

type DealItem = GetMyDealsQuery['getMyDeals']['data'][number];

interface DealCardProps {
  deal: DealItem;
  onClick: (id: string) => void;
}

/**
 * Formats a date string to a readable format
 */
function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function DealCard({ deal, onClick }: DealCardProps) {
  const stageName = deal.stage?.data?.name ?? 'Unknown Stage';
  const stageColor = deal.stage?.data?.color ?? '#6366f1';

  return (
    <Card
      className="cursor-pointer transition-all hover:shadow-md hover:border-primary/20"
      onClick={() => onClick(deal.id)}
      role="button"
      tabIndex={0}
      aria-label={`Deal: ${deal.title}, Value: ${formatCurrency(deal.value, deal.currency)}, Stage: ${stageName}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(deal.id);
        }
      }}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-foreground truncate">{deal.title}</h3>
            {deal.contact?.data && (
              <p className="text-sm text-muted-foreground mt-0.5 truncate">
                {deal.contact.data.firstName} {deal.contact.data.lastName}
                {deal.contact.data.company && ` · ${deal.contact.data.company}`}
              </p>
            )}
          </div>
          <Badge
            style={{
              backgroundColor: `${stageColor}20`,
              color: stageColor,
              borderColor: `${stageColor}40`,
            }}
            className="shrink-0 border"
          >
            {stageName}
          </Badge>
        </div>

        <div className="mt-3 flex items-center gap-4 text-sm text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <DollarSign className="h-3.5 w-3.5" />
            <span className="font-medium text-foreground">
              {formatCurrency(deal.value, deal.currency)}
            </span>
          </span>

          <span className="flex items-center gap-1.5">
            <TrendingUp className="h-3.5 w-3.5" />
            <span>{deal.probability}%</span>
          </span>

          <span className="flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5" />
            <span>{formatDate(deal.expectedCloseDate)}</span>
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

export default DealCard;
